import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  Paper,
  InputBase,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  Popper,
} from '@mui/material';
import { ArrowUp, RotateCcw, Video, X } from 'lucide-mui';

import { FilePreviewRouteParams } from '../types';
import {
  askMultimodalQuestion,
  deleteAskAISession,
  askQuestion,
  setVideoMode,
} from '../selectedVideoSlice';
import { RootState } from '../../../reducers';
import { useLimits } from '../../plan/hooks/limits';
import ChatLimitReached from './history/ChatLimitReached';
import { useTags } from '../../../hooks/useTags';

export default function CustomizedInputBase({
  chatBoxRef,
  noAudio,
  onLimitReached,
  showLimitMessage,
  onLimitMessageDismiss,
}: {
  chatBoxRef: React.RefObject<HTMLDivElement>;
  noAudio?: boolean;
  onLimitReached?: () => void;
  showLimitMessage?: boolean;
  onLimitMessageDismiss?: () => void;
}) {
  const [question, setQuestion] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const theme = useTheme();
  const guardLimits = useLimits();
  const { getTag, setTag } = useTags();
  const showLimitReached = guardLimits('askAI') <= 0;
  const { fileId } = useParams<FilePreviewRouteParams>();
  const { status, conversation, sessionId, isVideoMode } = useSelector(
    (state: RootState) => state.selectedVideo.chat
  );
  const { streams } = useSelector(
    (state: RootState) => state.selectedVideo.file
  );
  const { intent } = useSelector((state: RootState) => state.plan.marketing);
  const showVideoAnalyzerTooltip = React.useMemo(() => {
    const isDismissed = getTag('video-analyzer-tooltip-dismissed') === 'true';
    return (
      !isDismissed && (intent === 'video-analyzer' || intent === 'video-ask-ai')
    );
  }, [intent, getTag]);
  const hasVideo = streams?.includes('video');
  const dispatch = useDispatch();

  const getPlaceholderText = () => {
    if (noAudio && !isVideoMode) return 'Enable video analyzer mode';
    if (!hasVideo) return 'Ask anything about this Audio';
    if (isVideoMode) return 'Analyze video (Beta)';
    return 'Ask anything about the Video';
  };

  const handleVideoModeToggle = () => {
    if (!isVideoMode) {
      guardLimits('VideoAnalyzer', () => {
        dispatch(setVideoMode(true));
      });
    } else {
      dispatch(setVideoMode(false));
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const query = event.target.value;
    setQuestion(query);
    onLimitMessageDismiss?.();
  };

  const handleSearch = (event: React.FormEvent | React.KeyboardEvent) => {
    event.preventDefault();
    if (!fileId || !question) return;

    if (showLimitReached) {
      onLimitReached?.();
      return;
    }

    guardLimits('askAI', () => {
      if (isVideoMode) {
        dispatch(
          askMultimodalQuestion({
            fileId,
            promptText: question,
            mediaAnalysisOptions: {
              video: {
                segments: [],
              },
            },
          })
        );
      } else {
        dispatch(askQuestion({ fileId, question }));
      }
    });
    setTimeout(() => {
      if (chatBoxRef.current) {
        chatBoxRef.current.scrollTo({
          top: chatBoxRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 300);

    setQuestion('');
  };

  const handleClearChat = () => {
    if (fileId && sessionId) {
      dispatch(
        deleteAskAISession({
          fileId,
          sessionId,
        })
      );
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSearch(event);
    }
  };

  const handleDismissTooltip = () => {
    setTag({ key: 'video-analyzer-tooltip-dismissed', value: 'true' });
    setAnchorEl(null);
  };

  React.useEffect(() => {
    if (showVideoAnalyzerTooltip && !isVideoMode) {
      const element = document.querySelector('[data-video-analyzer-button]');
      if (element) {
        setAnchorEl(element as HTMLElement);
      }
    } else {
      setAnchorEl(null);
    }
  }, [showVideoAnalyzerTooltip, isVideoMode]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {showLimitMessage && <ChatLimitReached />}
      <Paper
        component="form"
        onSubmit={handleSearch}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          boxShadow: 'none',
          border: 'none',
          borderRadius: '9999px',
          backgroundColor: theme.palette.background.default,
          position: 'relative',
          opacity: noAudio && !isVideoMode ? 0.7 : 1,
        }}
      >
        {hasVideo && (
          <Box>
            <Popper
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              placement="top"
              sx={{ zIndex: theme.zIndex.tooltip + 1 }}
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ]}
            >
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'common.white',
                  borderRadius: 2,
                  boxShadow:
                    '0 2px 4px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.08)',
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderWidth: 8,
                    borderStyle: 'solid',
                    borderColor: 'white transparent transparent transparent',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {noAudio
                      ? 'Enable video analyzer to start asking questions'
                      : 'Click here to analyze the video content'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleDismissTooltip}
                    sx={{
                      padding: '2px',
                      marginLeft: '4px',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <X style={{ width: 14, height: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            </Popper>
            <Tooltip
              title={
                isVideoMode ? 'Video analyzer active' : 'Enable video analyzer'
              }
              placement="top"
              arrow
            >
              <IconButton
                onClick={handleVideoModeToggle}
                data-video-analyzer-button
                sx={{
                  ml: 1,
                  backgroundColor: isVideoMode
                    ? theme.palette.primary.main
                    : theme.palette.background.paper,
                  borderRadius: '9999px',
                  width: '40px',
                  height: '32px',
                  padding: '8px',
                  transition: 'all 0.2s ease-in-out',
                  border: `2px solid ${
                    isVideoMode
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  boxShadow: isVideoMode
                    ? 'none'
                    : `inset 0 0 0 1px ${theme.palette.divider}`,
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    left: isVideoMode ? '12px' : '2px',
                    top: '2px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: isVideoMode
                      ? theme.palette.primary.contrastText
                      : theme.palette.action.active,
                    transition: 'all 0.2s ease-in-out',
                    opacity: 0.1,
                  },
                  '&:hover': {
                    backgroundColor: isVideoMode
                      ? theme.palette.primary.dark
                      : theme.palette.background.paper,
                    borderColor: isVideoMode
                      ? theme.palette.primary.dark
                      : theme.palette.primary.main,
                  },
                  '& svg': {
                    transition: 'all 0.2s ease-in-out',
                    transform: isVideoMode
                      ? 'translateX(6px)'
                      : 'translateX(-6px)',
                    position: 'relative',
                    zIndex: 1,
                  },
                }}
              >
                <Video
                  style={{
                    color: isVideoMode
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                    width: '16px',
                    height: '16px',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <InputBase
          sx={{
            ml: hasVideo ? 1.5 : 2,
            flex: 1,
            height: 'auto',
            minHeight: '2.5rem',
            paddingRight: '40px',
          }}
          placeholder={getPlaceholderText()}
          inputProps={{ 'aria-label': 'ask ai', multiline: true }}
          value={question}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={status === 'pending' || (noAudio && !isVideoMode)}
          multiline
        />
        {question && status !== 'pending' && (
          <IconButton
            type="submit"
            sx={{
              position: 'absolute',
              bottom: '6px',
              right: '6px',
              zIndex: 1,
              backgroundColor: theme.palette.primary.main,
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              padding: '6px',
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
                '& .MuiSvgIcon-root': {
                  transform: 'translateY(-2px)',
                },
              },
            }}
          >
            <ArrowUp
              style={{
                transition: 'transform 0.2s ease-in-out',
                color: theme.palette.primary.contrastText,
                width: '16px',
                height: '16px',
              }}
            />
          </IconButton>
        )}
        {!question && conversation.length > 0 && status !== 'pending' && (
          <Tooltip title="New conversation" placement="top" arrow>
            <IconButton
              onClick={handleClearChat}
              sx={{
                position: 'absolute',
                bottom: '6px',
                right: '6px',
                zIndex: 1,
                backgroundColor: theme.palette.background.paper,
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  '& svg': {
                    transform: 'translateY(-2px)',
                  },
                },
              }}
            >
              <RotateCcw
                style={{
                  color: theme.palette.text.secondary,
                  transition: 'transform 0.2s ease-in-out',
                  width: '16px',
                  height: '16px',
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {status === 'pending' && (
          <CircularProgress
            size="1rem"
            sx={{
              color: theme.palette.secondary.dark,
              position: 'absolute',
              bottom: '10px',
              right: '10px',
            }}
          />
        )}
      </Paper>
    </Box>
  );
}
